import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Rocket, Store } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, getSource, track } from "@/lib/track";
import { LogoMark } from "@/components/Logo";
import QuizResultView from "@/components/QuizResult";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const STAGE_ICONS = { operating: Store, planning: Rocket };

export default function QuizPage() {
  const [config, setConfig] = useState(null);
  const [phase, setPhase] = useState("stage"); // stage | questions | contact | result
  const [trackId, setTrackId] = useState("");
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    axios
      .get(`${API_BASE}/quiz/config`)
      .then(({ data }) => setConfig(data))
      .catch(() => toast.error("Could not load the quiz. Please refresh the page."));
  }, []);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [idx, phase]);

  const trackMeta = config?.tracks?.[trackId];
  const questions = trackMeta?.questions || [];
  const q = questions[idx];
  const answeredCount = Object.keys(answers).length;

  const chooseStage = (tid) => {
    setTrackId(tid);
    setAnswers({});
    setIdx(0);
    setPhase("questions");
    track("quiz_started", { track: tid });
  };

  const choose = (optionId) => setAnswers({ ...answers, [q.id]: optionId });

  const next = () => {
    if (idx < questions.length - 1) setIdx(idx + 1);
    else setPhase("contact");
  };

  const back = () => {
    if (phase === "contact") setPhase("questions");
    else if (idx > 0) setIdx(idx - 1);
    else setPhase("stage");
  };

  const submit = async () => {
    const errs = {};
    if (!name.trim()) errs.name = "Please enter your first name.";
    if (!EMAIL_RE.test(email)) errs.email = "Please enter a valid email address.";
    setErrors(errs);
    if (Object.keys(errs).length > 0 || submittedRef.current || submitting) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/quiz/submit`, {
        track: trackId,
        answers,
        first_name: name.trim(),
        email: email.trim(),
        promo_consent: consent,
        source: getSource(),
      });
      setResult(data);
      setPhase("result");
    } catch (err) {
      submittedRef.current = false;
      setSubmitting(false);
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian px-5 py-16 sm:py-20 grain">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="quiz-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All In Hand Marketing
        </Link>

        {phase !== "result" && (
          <div className="mt-10">
            <LogoMark className="w-12 h-12" />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mt-6">
              Free · {config ? `${config.tracks.operating.question_count} questions · about 2 minutes` : ""}
            </p>
            <h1 className="font-display text-3xl sm:text-5xl font-bold text-white mt-3" data-testid="quiz-heading">
              {config?.name || "Marketing Foundations Check"}
            </h1>
            <p className="text-slate-300 mt-4 leading-relaxed text-sm sm:text-base">
              Ten quick questions about your marketing basics. You get a score, your strongest area, and the one
              gap worth fixing first — free.
            </p>
          </div>
        )}

        {phase === "stage" && (
          <div className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="quiz-stage-select">
            {config &&
              Object.entries(config.tracks).map(([tid, t]) => {
                const Icon = STAGE_ICONS[tid] || Store;
                return (
                  <button
                    key={tid}
                    data-testid={`quiz-stage-${tid}`}
                    onClick={() => chooseStage(tid)}
                    className="text-left rounded-3xl border border-slate-700/60 bg-obsidian-800/60 p-8 hover:border-gold/60 hover:bg-obsidian-800 transition-colors group"
                  >
                    <span className="inline-flex w-12 h-12 rounded-2xl border border-gold/40 items-center justify-center">
                      <Icon className="w-6 h-6 text-gold" />
                    </span>
                    <h2 className="font-display text-xl font-bold text-white mt-5 group-hover:text-gold transition-colors">
                      {t.label}
                    </h2>
                    <p className="text-slate-400 text-sm mt-2">{t.blurb}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-5 flex items-center gap-2">
                      Start <ArrowRight className="w-3.5 h-3.5" />
                    </p>
                  </button>
                );
              })}
          </div>
        )}

        {phase === "questions" && q && (
          <div className="mt-10" data-testid="quiz-question-screen">
            <div className="flex items-center gap-3" data-testid="quiz-progress">
              <div className="flex-1 h-1.5 rounded-full bg-obsidian-900 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gold transition-all duration-300"
                  style={{ width: `${((idx + 1) / questions.length) * 100}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 whitespace-nowrap">
                {idx + 1} / {questions.length}
              </span>
            </div>

            <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-8" data-testid="quiz-question-text">
              {q.q}
            </h2>

            <div className="mt-6 space-y-3">
              {q.options.map((o) => {
                const selected = answers[q.id] === o.id;
                return (
                  <button
                    key={o.id}
                    data-testid={`quiz-option-${o.id}`}
                    onClick={() => choose(o.id)}
                    aria-pressed={selected}
                    className={`w-full text-left rounded-2xl border px-5 py-4 text-sm transition-colors ${
                      selected
                        ? "border-gold bg-gold/10 text-white"
                        : "border-slate-700/60 bg-obsidian-800/40 text-slate-300 hover:border-gold/40"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
                          selected ? "border-gold bg-gold" : "border-slate-600"
                        }`}
                      >
                        {selected && <Check className="w-3 h-3 text-obsidian" />}
                      </span>
                      {o.label}
                      {o.na && <span className="text-[10px] text-slate-500 ml-auto shrink-0">excluded from score</span>}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid="quiz-back"
                onClick={back}
                className="rounded-full border border-slate-600 text-slate-200 font-display font-bold px-8 py-4 hover:border-gold hover:text-gold transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                data-testid="quiz-next"
                onClick={next}
                disabled={!answers[q.id]}
                className="flex-1 max-w-xs rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {idx === questions.length - 1 ? "Almost done" : "Next"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {phase === "contact" && (
          <div className="mt-10" data-testid="quiz-contact-screen">
            <div className="rounded-3xl border border-gold/20 bg-obsidian-800/60 p-7 sm:p-10">
              <h2 className="font-display text-2xl font-bold text-white" data-testid="quiz-contact-heading">
                Where should we send your result?
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed mt-3">
                Your first name and email are <span className="text-gold font-semibold">required</span> — we use them
                to calculate, save and send your free result. Your result lives at a private link only you have.
                Promotional emails are opt-in only: the checkbox below is completely optional.
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="quiz-name" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-300 mb-2">
                    First name *
                  </label>
                  <input
                    id="quiz-name"
                    data-testid="quiz-input-name"
                    className="w-full rounded-xl border border-slate-600 bg-obsidian-900/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jordan"
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && <p data-testid="quiz-error-name" className="text-red-300 text-xs mt-2">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="quiz-email" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-300 mb-2">
                    Email *
                  </label>
                  <input
                    id="quiz-email"
                    type="email"
                    data-testid="quiz-input-email"
                    className="w-full rounded-xl border border-slate-600 bg-obsidian-900/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <p data-testid="quiz-error-email" className="text-red-300 text-xs mt-2">{errors.email}</p>}
                </div>
              </div>

              <label
                htmlFor="quiz-consent"
                className="flex gap-3 items-start mt-6 cursor-pointer select-none"
              >
                <input
                  id="quiz-consent"
                  data-testid="quiz-consent-checkbox"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#D4AF37]"
                />
                <span className="text-xs text-slate-300 leading-relaxed">
                  Optional: email me occasional marketing tips and offers from All In Hand Marketing. I can
                  unsubscribe any time.
                </span>
              </label>

              <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="button"
                  data-testid="quiz-contact-back"
                  onClick={back}
                  className="sm:w-auto rounded-full border border-slate-600 text-slate-200 font-display font-bold px-8 py-4 hover:border-gold hover:text-gold transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid="quiz-submit"
                  onClick={submit}
                  disabled={submitting}
                  className="flex-1 w-full rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {submitting ? "Scoring your answers…" : "Show My Result"}
                </button>
              </div>
              <p className="text-slate-500 text-xs text-center mt-4">
                {config?.disclaimer}
              </p>
            </div>
          </div>
        )}

        {phase === "result" && result && (
          <div className="mt-10" data-testid="quiz-result-screen">
            <LogoMark className="w-12 h-12" />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mt-6">Marketing Foundations Check</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3" data-testid="quiz-result-greeting">
              {result.first_name ? `Thanks, ${result.first_name} — here's your result` : "Here's your result"}
            </h1>
            {result.email_sent ? (
              <p className="text-emerald-400 text-sm mt-3" data-testid="quiz-email-confirm">
                A copy is on its way to your inbox — it includes one practical action you can take today.
              </p>
            ) : (
              <p className="text-slate-400 text-sm mt-3" data-testid="quiz-email-confirm">
                We could not send the email copy just now — this page is your result, and it stays available at this
                address.
              </p>
            )}
            <QuizResultView result={result} />
          </div>
        )}
      </div>
    </div>
  );
}
